// Breadcrumb trail label store (docs/design-system/breadcrumb-trail.md §5, §9).
//
// The shell renders ONE trail for every routed page (`cos-trail`). Static
// crumbs come from navigation/trail-registry.ts; the labels that only the
// PAGE can know are published here, and there are two kinds:
//
//   setLeaf(label)              the CURRENT page's own name — a partner, a
//                               contract number, a thread subject.
//   setCrumbLabel(href, label)  an INTERMEDIATE crumb's name. The registry
//                               can only put the URL's own id there (the
//                               partner between Partners and Implementation,
//                               the contract between Contracts and Amend),
//                               and §4.3 says a trail never shows a bare id.
//                               The page that knows the name says so, keyed
//                               by the href the crumb links to.
//
// The shell subscribes and clears BOTH on every route change. Clearing is not
// enough on its own, though, and there are two ways a label outlives its page:
//
//   1. It is published a beat late, while the URL still says the old page.
//      Every label is STAMPED with the route it was published for, and the
//      shell shows only what belongs to the route it is rendering.
//   2. It is published AFTER the route changed. Lit flushes an update that
//      was already queued even once the element is disconnected, and thirteen
//      record pages publish their leaf from an unconditional `updated()`, so
//      this is the common case — and by then `location.pathname` is already
//      the NEW page, which is why the stamp cannot catch it. The only thing
//      that distinguishes the straggler is that its publisher is no longer in
//      the document, so a page passes `this` and a disconnected publisher is
//      ignored. `lint/trail-leaf-host.test.ts` requires the argument wherever
//      a component publishes from `updated()`.
//
// Pages never render breadcrumbs or Back links of their own.

export type TrailLeafListener = (leaf: string | null) => void;

/** A published label plus the route it was published for. */
interface Stamped {
	label: string;
	path: string;
	search: string;
}

let currentLeaf: Stamped | null = null;
let crumbLabels = new Map<string, Stamped>();
const listeners = new Set<TrailLeafListener>();

function notify(): void {
	for (const fn of listeners) fn(currentLeaf?.label ?? null);
}

function here(): { path: string; search: string } {
	if (typeof window === 'undefined') return { path: '', search: '' };
	return { path: window.location.pathname, search: window.location.search };
}

// Query keys that change which leaf a route resolves to: Circulate's module
// tab and the workspace hint on the paths both rails share. Every other
// param (a filter, a tab, a collection_id) leaves the trail alone, so it must
// not invalidate a label the page already published.
const TRAIL_QUERY_KEYS = ['m', 'view'];

/** Two routes are the same PAGE for trail purposes when the pathname matches
 *  and neither of the trail-bearing query keys has changed. */
export function sameTrailRoute(
	a: { path: string; search: string },
	b: { path: string; search: string },
): boolean {
	if (a.path !== b.path) return false;
	const qa = new URLSearchParams(a.search);
	const qb = new URLSearchParams(b.search);
	return TRAIL_QUERY_KEYS.every((k) => (qa.get(k) ?? '') === (qb.get(k) ?? ''));
}

function belongsTo(stamped: Stamped, path: string, search: string): boolean {
	return sameTrailRoute(stamped, { path, search });
}

/** Crumb labels are keyed by PATH: a page publishing `/partners/44` must
 *  still match a crumb whose href carries a query. */
function keyFor(href: string): string {
	const trimmed = (href ?? '').trim();
	const q = trimmed.search(/[?#]/);
	return q === -1 ? trimmed : trimmed.slice(0, q);
}

/** The publishing element. A page passes `this` so a label from an element
 *  the router has already torn down is dropped rather than decorating
 *  whatever the user opened next. */
export interface TrailPublisher {
	isConnected: boolean;
}

/** True when this publication should be dropped: its publisher has left the
 *  document, so whatever it is naming is no longer on screen. */
function publisherIsGone(host: TrailPublisher | undefined): boolean {
	return host !== undefined && !host.isConnected;
}

/** Supply the current page's label. Empty or whitespace clears it.
 *
 *  Pass `this` from a component: a leaf published by a disconnected element
 *  is a straggler from the previous page and is ignored. */
export function setLeaf(label: string | null | undefined, host?: TrailPublisher): void {
	if (publisherIsGone(host)) return;
	const next = typeof label === 'string' && label.trim() ? label.trim() : null;
	if (next === null) {
		if (currentLeaf === null) return;
		currentLeaf = null;
		notify();
		return;
	}
	const at = here();
	if (currentLeaf?.label === next && currentLeaf.path === at.path && currentLeaf.search === at.search) return;
	currentLeaf = { label: next, ...at };
	notify();
}

/** Name an INTERMEDIATE crumb, by the href it links to. The shell swaps the
 *  label in wherever that crumb appears; a crumb nobody named keeps whatever
 *  the registry gave it (usually the id from the URL). Pass `this` from a
 *  component, for the reason `setLeaf` gives. */
export function setCrumbLabel(
	href: string | null | undefined,
	label: string | null | undefined,
	host?: TrailPublisher,
): void {
	if (publisherIsGone(host)) return;
	const key = keyFor(typeof href === 'string' ? href : '');
	if (!key) return;
	const next = typeof label === 'string' && label.trim() ? label.trim() : null;
	if (next === null) {
		if (!crumbLabels.has(key)) return;
		// A fresh Map every time: the shell holds this in reactive state, and
		// Lit compares by identity.
		crumbLabels = new Map(crumbLabels);
		crumbLabels.delete(key);
		notify();
		return;
	}
	const at = here();
	const held = crumbLabels.get(key);
	if (held && held.label === next && held.path === at.path && held.search === at.search) return;
	crumbLabels = new Map(crumbLabels);
	crumbLabels.set(key, { label: next, ...at });
	notify();
}

/** Every crumb label published, whatever route it was published for. The
 *  shell wants `crumbLabelsFor` instead. */
export function currentCrumbLabels(): ReadonlyMap<string, string> {
	const out = new Map<string, string>();
	for (const [key, stamped] of crumbLabels) out.set(key, stamped.label);
	return out;
}

/** The leaf, but only if it was published FOR this route. A page's last
 *  render can land after the user has already moved on — Lit flushes an
 *  update queued before the element disconnected — and by then
 *  `location.pathname` is the new page, so the stamp is what tells the two
 *  apart when the timing is kinder than that. */
export function trailLeafFor(path: string, search: string): string | null {
	if (!currentLeaf) return null;
	return belongsTo(currentLeaf, path, search) ? currentLeaf.label : null;
}

/** The crumb labels published FOR this route. */
export function crumbLabelsFor(path: string, search: string): ReadonlyMap<string, string> {
	const out = new Map<string, string>();
	for (const [key, stamped] of crumbLabels) {
		if (belongsTo(stamped, path, search)) out.set(key, stamped.label);
	}
	return out;
}

/** Forget the leaf. Prefer `clearTrail()` on a route change — a crumb label
 *  outlives its page just as readily as a leaf does. */
export function clearLeaf(): void {
	setLeaf(null);
}

/** Forget everything the last page published. The shell calls this on every
 *  route change. */
export function clearTrail(): void {
	if (currentLeaf === null && crumbLabels.size === 0) return;
	currentLeaf = null;
	if (crumbLabels.size > 0) crumbLabels = new Map();
	notify();
}

/** Test seam: publish a label as if the page had been at `path` + `search`,
 *  which is the one thing a unit test cannot arrange for real. */
export function _setLeafAtForTests(label: string, path: string, search = ''): void {
	currentLeaf = { label, path, search };
	notify();
}

/** The leaf as last supplied, whatever route it was supplied for. The shell
 *  wants `trailLeafFor` instead. */
export function currentTrailLeaf(): string | null {
	return currentLeaf?.label ?? null;
}

/** Subscribe to any change here — the leaf or a crumb label. The callback
 *  fires immediately with the current leaf, and again whenever anything is
 *  published; the shell re-reads the route-filtered values on the re-render
 *  that follows. Returns an unsubscribe function. */
export function subscribeLeaf(fn: TrailLeafListener): () => void {
	listeners.add(fn);
	fn(currentLeaf?.label ?? null);
	return () => {
		listeners.delete(fn);
	};
}

export function _resetTrailForTests(): void {
	currentLeaf = null;
	crumbLabels = new Map();
	listeners.clear();
}
